<script lang="ts" setup>
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import { VDataTableServer } from "vuetify/labs/VDataTable";

// Interface
interface Emit {
  (e: "closeDialog", value: boolean): void;
}

interface Props {
  isVisible?: boolean;
  docketId: string;
}

// Props
const props = defineProps<Props>();

// Emit
const emit = defineEmits<Emit>();

// Data
const docketId = ref<string>("");
const listHistory = ref<any[]>([]);
const headers = [
  { title: "Time", key: "updated", sortable: true },
  { title: "Email", key: "email", sortable: true },
  { title: "Printed", key: "printed", sortable: true },
  { title: "Emailed", key: "emailed", sortable: true },
] as any[];
const options = ref<any>({
  page: 1,
  itemsPerPage: 10,
  sortBy: [],
  groupBy: [],
  search: undefined,
});
const isLoading = ref(false);
const totalList = ref<number>(0);

// Method
const fetchDocketHistory = async () => {
  try {
    if (isLoading.value) return;
    isLoading.value = true;

    let filter = `docketID = "${docketId.value}"`;
    let sortParam = "";
    if (options.value?.sortBy.length) {
      sortParam =
        options.value?.sortBy[0].order === "desc"
          ? `-${options.value?.sortBy[0].key}`
          : options.value?.sortBy[0].key;
    }

    const records = await pb
      .collection("docketReceipts")
      .getList(options.value?.page, options.value?.itemsPerPage, {
        filter: filter,
        sort: sortParam,
      });

    if (records) {
      listHistory.value = records?.items;
      totalList.value = records.totalItems || 0;
      options.value.page = records.page;
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const closeDialog = () => {
  emit("closeDialog", false);
};

// watch
watch(
  props,
  async (newValue, oldValue) => {
    if (newValue?.docketId && newValue?.isVisible) {
      docketId.value = newValue?.docketId;
      await fetchDocketHistory();
    }
  },
  { immediate: true }
);
</script>

<template>
  <VDialog
    persistent
    :model-value="props.isVisible"
    no-click-animation
    width="900"
    @update:is-drawer-open="closeDialog"
  >
    <VCard flat>
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <!-- 👉 Header -->
        <VCardTitle> Docket History </VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="closeDialog"
        ></VBtn>
      </div>
      <div>
        <VDataTableServer
          v-model:items-per-page="options.itemsPerPage"
          v-model:page="options.page"
          :loading="isLoading"
          :items-length="totalList"
          :headers="headers"
          :items="listHistory"
          class="text-no-wrap rounded-0"
          @update:options="
            ($event) => {
              options = $event;
              fetchDocketHistory();
            }
          "
        >
          <template #item.updated="{ item }">
            <span class="text-sm">
              {{
                item.raw.updated
                  ? moment(item.raw.updated).format("DD-MM hh:mm A")
                  : ""
              }}
            </span>
          </template>

          <template #item.email="{ item }">
            <span class="text-sm"> {{ item.raw.email }} </span>
          </template>

          <template #item.printed="{ item }">
            <VCheckbox :model-value="item?.raw?.printed" readonly />
          </template>

          <template #item.emailed="{ item }">
            <VCheckbox :model-value="item?.raw?.emailed" readonly />
          </template>

          <!-- pagination  -->
          <template #bottom>
            <VDivider />
            <div class="d-flex gap-x-6 flex-wrap justify-end pa-2 align-center">
              <div class="text-sm ms-2">{{ totalList }} Records</div>
              <VSpacer />
              <div class="d-flex align-center gap-x-2 text-sm">
                Rows Per Page:
                <VSelect
                  v-model="options.itemsPerPage"
                  variant="plain"
                  class="per-page-select text-high-emphasis"
                  density="compact"
                  :items="[10, 20, 25, 50, 100]"
                  @update:modelValue="fetchDocketHistory()"
                />
              </div>
              <div class="d-flex text-sm align-center text-high-emphasis" />
              <div class="d-flex gap-x-2 align-center">
                <VBtn
                  class="flip-in-rtl"
                  icon="mdi-chevron-left"
                  variant="text"
                  density="comfortable"
                  color="default"
                  :disabled="options.page <= 1"
                  @click="
                    () => {
                      options.page <= 1 ? (options.page = 1) : options.page--,
                        fetchDocketHistory();
                    }
                  "
                />

                <VBtn
                  class="flip-in-rtl"
                  icon="mdi-chevron-right"
                  density="comfortable"
                  variant="text"
                  color="default"
                  :disabled="
                    options.page >= Math.ceil(totalList / options.itemsPerPage)
                  "
                  @click="
                    () => {
                      options.page >=
                      Math.ceil(totalList / options.itemsPerPage)
                        ? (options.page = Math.ceil(
                            totalList / options.itemsPerPage
                          ))
                        : options.page++,
                        fetchDocketHistory();
                    }
                  "
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>
  </VDialog>
</template>
